import React, { useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { GroupedGuidedVerifyEvidenceActions } from './GroupedGuidedVerifyEvidenceActions';
import { IncidentDrawerRepresentativeEvidenceIntro } from './IncidentDrawerRepresentativeEvidenceIntro';
import { IncidentDrawerEvidenceTruthPanel } from './IncidentDrawerEvidenceTruthPanel';
import { IncidentDrawerDetailStep2Clip } from './IncidentDrawerDetailStep2Clip';
import { IncidentDrawerDetailStep3 } from './IncidentDrawerDetailStep3';
import { useGroupedVerifyEvidenceAutoPromote } from '../../pages/incidents/useGroupedVerifyEvidenceAutoPromote';
import { useGroupedVerifyEvidenceBetterProof } from '../../pages/incidents/useGroupedVerifyEvidenceBetterProof';
import { groupedEvidenceReviewHasCloudProof } from '../../domain/incidents/groupedEvidenceRepresentativePick';

type VerifySectionProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

const VerifySection: React.FC<VerifySectionProps> = ({ title, subtitle, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 2,
      borderRadius: 3,
      borderColor: '#d0d7e2',
      bgcolor: '#ffffff',
    }}
  >
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 1.25, lineHeight: 1.55 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

/** Evidence-first review: viewer (snapshot + clip) → decision → optional technical detail. */
export function GroupedVerifyEvidencePanel({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as Record<string, any>;
  const autoPromote = useGroupedVerifyEvidenceAutoPromote({
    incident: s.selectedIncident,
    drawerBusy: s.drawerBusy,
    onAfterPromote: s._refreshSelectedIncident,
  });
  const reviewTimeRange = (s.reviewTimeRange || {}) as { fromDate?: string; toDate?: string };
  const betterProof = useGroupedVerifyEvidenceBetterProof({
    incident: s.selectedIncident,
    fromDate: reviewTimeRange.fromDate,
    toDate: reviewTimeRange.toDate,
  });
  const hasCloudProof = groupedEvidenceReviewHasCloudProof(s.selectedIncident);
  const reviewExactMode = Boolean(s.reviewExactMode);
  const reviewHeatmapHourActive = Boolean(s.reviewHeatmapHourActive);
  const selectedIncidentId = String(s.selectedIncident?.incident_id || '').trim();
  const suppressAutoBetterProof = Boolean(s.suppressAutoBetterProof);
  const onOpenReviewPageFullProof = s.onOpenReviewPageFullProof as ((incidentId: string) => void) | undefined;
  const autoOpenedBetterProofRef = useRef<string>('');

  useEffect(() => {
    const betterId = String(betterProof.betterProofIncidentId || '').trim();
    const currentId = selectedIncidentId;
    // Allow hop even when current already has cloud proof — series reconcile may still
    // find a newer evidence-complete occurrence after outages / delayed uploads.
    if (!betterId || !currentId || betterId === currentId || betterProof.loading) return;
    if (reviewExactMode || reviewHeatmapHourActive || suppressAutoBetterProof) return;
    if (typeof onOpenReviewPageFullProof !== 'function') return;
    const hopKey = `${currentId}->${betterId}`;
    if (autoOpenedBetterProofRef.current === hopKey) return;
    autoOpenedBetterProofRef.current = hopKey;
    onOpenReviewPageFullProof(betterId);
  }, [
    betterProof.betterProofIncidentId,
    betterProof.loading,
    onOpenReviewPageFullProof,
    reviewExactMode,
    reviewHeatmapHourActive,
    selectedIncidentId,
    suppressAutoBetterProof,
  ]);

  return (
    <Box data-testid="grouped-verify-evidence-panel">
      <VerifySection title="Evidence">
        {!hasCloudProof ? (
          <Alert severity="warning" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="grouped-verify-incomplete-proof">
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              Full cloud video is not ready for this occurrence yet.
              {betterProof.loading ? (
                <> Checking for another occurrence with video…</>
              ) : betterProof.betterProofIncidentId ? (
                <> Opening an occurrence that already has snapshot and video.</>
              ) : (
                <> Clip may still be uploading from the edge — refresh below if it stays pending.</>
              )}
            </Typography>
            {betterProof.betterProofIncidentId && typeof onOpenReviewPageFullProof === 'function' ? (
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1, fontWeight: 700 }}
                onClick={() => onOpenReviewPageFullProof(betterProof.betterProofIncidentId!)}
                data-testid="grouped-verify-open-better-proof"
              >
                Open occurrence with video
              </Button>
            ) : null}
          </Alert>
        ) : null}
        {autoPromote.message ? (
          <Alert severity="info" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="grouped-verify-auto-promote-status">
            {autoPromote.message}
          </Alert>
        ) : null}
        {autoPromote.error ? (
          <Alert severity="warning" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="grouped-verify-auto-promote-error">
            {autoPromote.error}
          </Alert>
        ) : null}

        {/* Snapshot preview + metadata; dedicated clip player below owns video playback. */}
        <IncidentDrawerRepresentativeEvidenceIntro scope={scope} preferSnapshotPreview />

        <Box sx={{ mt: 1.5 }} data-testid="grouped-verify-clip-player">
          <IncidentDrawerDetailStep2Clip scope={scope} />
        </Box>
      </VerifySection>

      <VerifySection
        title="Reviewer decision"
        subtitle="Record the decision after reviewing the evidence above."
      >
        <GroupedGuidedVerifyEvidenceActions scope={scope} />
      </VerifySection>

      <Accordion
        disableGutters
        data-testid="grouped-verify-technical-accordion"
        sx={{
          mb: 1.25,
          borderRadius: '12px !important',
          border: '1px solid #d0d7e2',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 800 }}>Technical details</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
          <IncidentDrawerEvidenceTruthPanel scope={scope} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        data-testid="grouped-verify-detection-accordion"
        sx={{
          mb: 1.25,
          borderRadius: '12px !important',
          border: '1px solid #d0d7e2',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography sx={{ fontWeight: 800 }}>Detection trigger (optional)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              Rule, match basis, and recurrence observations — open when you need detailed audit context.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
          <IncidentDrawerDetailStep3 scope={scope} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default GroupedVerifyEvidencePanel;
